function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { nanoid } from 'nanoid';
import throttle from 'lodash.throttle';
/**
 * Exports a function for initializing monaco with the deephaven theme/config
 */
import { resolveCssVariablesInRecord } from '@deephaven/components';
import { assertNotNull } from '@deephaven/utils';
import { find as linkifyFind } from 'linkifyjs';
import * as monaco from 'monaco-editor';
// @ts-ignore
import { KeyCodeUtils } from 'monaco-editor/esm/vs/base/common/keyCodes.js';
import Log from '@deephaven/log';
import MonacoThemeRaw from "./MonacoTheme.module.css";
import PyLang from "./lang/python.js";
import GroovyLang from "./lang/groovy.js";
import ScalaLang from "./lang/scala.js";
import DbLang from "./lang/db.js";
import LogLang from "./lang/log.js";
import MonacoProviders from "./MonacoProviders.js";
var log = Log.module('MonacoUtils');
var CONSOLE_URI_PREFIX = 'inmemory://dh-console/';
class MonacoUtils {
  /**
   * Initializes Monaco for the environment
   * @param getWorker The getWorker function Monaco should use
   *                  The workers should be provided by the caller and bundled by their build system (e.g. Vite, Webpack)
   */
  static init() {
    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      getWorker = _ref.getWorker;
    log.debug('Initializing Monaco...');
    if (getWorker !== undefined) {
      MonacoUtils.registerGetWorker(getWorker);
    }
    var initTheme = MonacoUtils.initTheme,
      registerLanguages = MonacoUtils.registerLanguages;
    initTheme();
    registerLanguages([DbLang, PyLang, GroovyLang, LogLang, ScalaLang]);
    monaco.languages.onLanguage('python', () => {
      monaco.languages.registerCodeActionProvider('python', {
        provideCodeActions: MonacoProviders.handlePythonCodeActionRequest
      }, {
        providedCodeActionKinds: ['quickfix']
      });
      monaco.languages.registerDocumentFormattingEditProvider('python', {
        provideDocumentFormattingEdits: MonacoProviders.handlePythonFormatRequest
      });
    });
    monaco.editor.onDidCreateModel(model => {
      // Lint Python models on creation and on change
      if (model.getLanguageId() === 'python') {
        if (MonacoProviders.ruffWorkspace != null) {
          MonacoProviders.lintPython(model);
        }
        var throttledLint = throttle(m => MonacoProviders.lintPython(m), 250);
        model.onDidChangeContent(() => {
          throttledLint(model);
        });
      }
    });
    MonacoUtils.removeConflictingKeybindings();
    log.debug('Monaco initialized.');
  }

  /**
   * Initialize current Monaco theme based on the current DH theme.
   */
  static initTheme() {
    var _MonacoTheme$logErro, _MonacoTheme$logErro2;
    var removeHashtag = MonacoUtils.removeHashtag;
    var MonacoTheme = resolveCssVariablesInRecord(MonacoThemeRaw);
    log.debug2('Monaco theme:', MonacoThemeRaw);
    log.debug2('Monaco theme derived:', MonacoTheme);
    var dhDarkRules = [{
      token: '',
      foreground: removeHashtag(MonacoTheme.foreground)
    }, {
      token: 'string',
      foreground: removeHashtag(MonacoTheme.string)
    }, {
      token: 'string.delim',
      foreground: removeHashtag(MonacoTheme['string-delim'])
    }, {
      token: 'keyword',
      foreground: removeHashtag(MonacoTheme.keyword)
    }, {
      token: 'identifier.js',
      foreground: removeHashtag(MonacoTheme['identifier-js'])
    }, {
      token: 'delimiter',
      foreground: removeHashtag(MonacoTheme.delimiter)
    }, {
      token: 'comment',
      foreground: removeHashtag(MonacoTheme.comment)
    }, {
      token: 'number',
      foreground: removeHashtag(MonacoTheme.number)
    }, {
      token: 'storage',
      foreground: removeHashtag(MonacoTheme.storage)
    }, {
      token: 'identifier',
      foreground: removeHashtag(MonacoTheme.identifier)
    }, {
      token: 'namespace.identifier',
      foreground: removeHashtag(MonacoTheme['namespace-identifier'])
    }, {
      token: 'operator',
      foreground: removeHashtag(MonacoTheme.operator)
    }, {
      token: 'predefined',
      foreground: removeHashtag(MonacoTheme.predefined)
    }, {
      token: 'error.log',
      foreground: (_MonacoTheme$logErro = (_MonacoTheme$logErro2 = MonacoTheme['log-error']) === null || _MonacoTheme$logErro2 === void 0 ? void 0 : _MonacoTheme$logErro2.substring(1)) !== null && _MonacoTheme$logErro !== void 0 ? _MonacoTheme$logErro : ''
    }, {
      token: 'warn.log',
      foreground: removeHashtag(MonacoTheme['log-warn'])
    }, {
      token: 'info.log',
      foreground: removeHashtag(MonacoTheme['log-info'])
    }, {
      token: 'stdout.log',
      foreground: removeHashtag(MonacoTheme['log-stdout'])
    }, {
      token: 'trace.log',
      foreground: removeHashtag(MonacoTheme['log-trace'])
    }, {
      token: 'debug.log',
      foreground: removeHashtag(MonacoTheme['log-debug'])
    }, {
      token: 'date.log',
      foreground: removeHashtag(MonacoTheme['log-date'])
    }];
    var dhDarkColors = {
      foreground: MonacoTheme.foreground,
      errorForeground: MonacoTheme['error-foreground'],
      'inputOption.activeBorder': MonacoTheme['input-option-active-border'],
      'editor.background': MonacoTheme.background,
      'editor.foreground': MonacoTheme.foreground,
      'editor.lineHighlightBackground': MonacoTheme['editor-line-highlight-background'],
      'editorLineNumber.foreground': MonacoTheme['editor-line-number-foreground'],
      'editorLineNumber.activeForeground': MonacoTheme['editor-line-number-active-foreground'],
      'editor.selectionBackground': MonacoTheme['editor-selection-background'],
      'editor.inactiveSelectionBackground': MonacoTheme['editor-inactive-selection-background'],
      'editor.selectionHighlightBackground': MonacoTheme['editor-selection-highlight-background'],
      'editor.wordHighlightBackground': MonacoTheme['editor-word-highlight-background'],
      'editor.findMatchBackground': MonacoTheme['editor-find-match-background'],
      'editor.findMatchHighlightBackground': MonacoTheme['editor-find-match-highlight-background'],
      'editorSuggestWidget.background': MonacoTheme['editor-suggest-widget-background'],
      'editorSuggestWidget.border': MonacoTheme['editor-suggest-widget-border'],
      'editorSuggestWidget.foreground': MonacoTheme['editor-suggest-widget-foreground'],
      'editorSuggestWidget.selectedBackground': MonacoTheme['editor-suggest-widget-selected-background'],
      'list.activeSelectionBackground': MonacoTheme['editor-suggest-widget-selected-background'],
      'editorSuggestWidget.selectedForeground': MonacoTheme['editor-suggest-widget-selected-foreground'],
      'list.activeSelectionForeground': MonacoTheme['editor-suggest-widget-selected-foreground'],
      'editorSuggestWidget.highlightForeground': MonacoTheme['editor-suggest-widget-highlightForeground'],
      'editorSuggestWidget.focusHighlightForeground': MonacoTheme['editor-suggest-widget-focus-highlight-foreground'],
      'list.hoverBackground': MonacoTheme['list-hover-background'],
      'dropdown.background': MonacoTheme['context-menu-background'],
      'dropdown.foreground': MonacoTheme['context-menu-foreground'],
      'menu.selectionBackground': MonacoTheme['menu-selection-background'],
      'menu.selectionForeground': MonacoTheme['menu-selection-foreground'],
      'keybindingLabel.foreground': MonacoTheme['menu-selection-foreground'],
      'list.focusBackground': MonacoTheme['menu-selection-background'],
      'editorWidget.background': MonacoTheme['editor-widget-background'],
      'inputOption.activeBackground': MonacoTheme['input-option-active-background'],
      'inputOption.activeForeground': MonacoTheme['input-option-active-foreground'],
      focusBorder: MonacoTheme['focus-border'],
      'input.background': MonacoTheme['input-background'],
      'input.foreground': MonacoTheme['input-foreground'],
      'input.border': MonacoTheme['input-border'],
      'textLink.foreground': MonacoTheme['text-link-foreground'],
      'textLink.activeForeground': MonacoTheme['text-link-active-foreground'],
      'editorLink.activeForeground': MonacoTheme['editor-link-active-foreground'],
      'editorBracketHighlight.foreground1': MonacoTheme['editor-bracket-foreground1'],
      'editorBracketHighlight.foreground2': MonacoTheme['editor-bracket-foreground2'],
      'editorBracketHighlight.foreground3': MonacoTheme['editor-bracket-foreground3'],
      'editorBracketHighlight.foreground4': MonacoTheme['editor-bracket-foreground4'],
      'editorBracketHighlight.foreground5': MonacoTheme['editor-bracket-foreground5'],
      'editorBracketHighlight.foreground6': MonacoTheme['editor-bracket-foreground6'],
      'editorBracketHighlight.unexpectedBracket.foreground': MonacoTheme['editor-unexpected-bracket-foreground'],
      'diffEditor.insertedTextBackground': MonacoTheme['diff-editor-inserted-text-background'],
      'diffEditor.removedTextBackground': MonacoTheme['diff-editor-removed-text-background'],
      'diffEditor.insertedLineBackground': MonacoTheme['diff-editor-inserted-line-background'],
      'diffEditor.removedLineBackground': MonacoTheme['diff-editor-removed-line-background']
    };
    monaco.editor.defineTheme('dh-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: dhDarkRules,
      colors: dhDarkColors
    });
    try {
      monaco.editor.setTheme('dh-dark');
    } catch (err) {
      log.error("Failed to set 'dh-dark' Monaco theme, falling back to vs-dark", err);
      monaco.editor.setTheme('vs-dark');
    }
  }

  /**
   * Register the getWorker function for Monaco
   * @param getWorker The getWorker function for Monaco
   */
  static registerGetWorker(getWorker) {
    window.MonacoEnvironment = _objectSpread(_objectSpread({}, window.MonacoEnvironment), {}, {
      getWorker
    });
  }

  /**
   * Remove the hashtag prefix from a CSS color string.
   * Monaco expects colors to be the value only, no hashtag.
   * @param color The hex color string to remove the hashtag from, eg. '#ffffff'
   */
  static removeHashtag(color) {
    var _color$substring;
    return (_color$substring = color === null || color === void 0 ? void 0 : color.substring(1)) !== null && _color$substring !== void 0 ? _color$substring : '';
  }
  static registerLanguages(languages) {
    // First override the default loader for any language we have a custom definition for
    // https://github.com/Microsoft/monaco-editor/issues/252#issuecomment-482786867
    var languageIds = languages.map(_ref2 => {
      var id = _ref2.id;
      return id;
    });
    monaco.languages.getLanguages().filter(_ref3 => {
      var id = _ref3.id;
      return languageIds.includes(id);
    }).forEach(languageParam => {
      var language = languageParam;
      log.debug2('Overriding default language loader:', language.id);
    });

    // Then register our language definitions
    languages.forEach(language => {
      MonacoUtils.registerLanguage(language);
    });
  }
  static registerLanguage(language) {
    log.debug2('Registering language: ', language.id);
    monaco.languages.register(language);
    monaco.languages.onLanguage(language.id, () => {
      monaco.languages.setLanguageConfiguration(language.id, language.conf);
      monaco.languages.setMonarchTokensProvider(language.id, language.language);
    });
  }

  /**
   * Set EOL preference for the editor
   * @param editor The editor to set the EOL for
   * @param eolSequence EOL sequence
   */
  static setEOL(editor) {
    var _editor$getModel;
    var eolSequence = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : monaco.editor.EndOfLineSequence.LF;
    (_editor$getModel = editor.getModel()) === null || _editor$getModel === void 0 || _editor$getModel.setEOL(eolSequence);
  }

  /**
   * Links an editor with a provided session to provide completion items.
   * @param session The IdeSession to link
   * @param editor The editor to link the session to
   * @returns A cleanup function for disposing of the created listeners
   */
  static openDocument(editor, session) {
    var model = editor.getModel();
    assertNotNull(model);
    var didOpenDocumentParams = {
      textDocument: {
        uri: "".concat(model.uri),
        languageId: model.getLanguageId(),
        version: model.getVersionId(),
        text: model.getValue()
      }
    };
    log.debug2('didOpenDocumentParams: ', didOpenDocumentParams);
    session.openDocument(didOpenDocumentParams);
    var dispose = editor.onDidChangeModelContent(changedEvent => {
      var changes = changedEvent.changes,
        versionId = changedEvent.versionId;
      var contentChanges = [];
      for (var i = 0; i < changes.length; i += 1) {
        var change = changes[i];
        var range = change.range,
          rangeLength = change.rangeLength,
          text = change.text;
        var contentChange = {
          range: {
            start: {
              line: range.startLineNumber - 1,
              character: range.startColumn - 1
            },
            end: {
              line: range.endLineNumber - 1,
              character: range.endColumn - 1
            }
          },
          rangeLength,
          text
        };
        contentChanges.push(contentChange);
      }
      if (contentChanges.length > 0) {
        var didChangeDocumentParams = {
          textDocument: {
            version: versionId,
            uri: "".concat(model.uri)
          },
          contentChanges
        };
        log.debug2('didChangeDocumentParams', didChangeDocumentParams);
        session.changeDocument(didChangeDocumentParams);
      }
    });
    return dispose;
  }
  static closeDocument(editor, session) {
    var model = editor.getModel();
    assertNotNull(model);
    var didCloseDocumentParams = {
      textDocument: {
        uri: "".concat(model.uri)
      }
    };
    session.closeDocument(didCloseDocumentParams);
  }

  /**
   * Register a paste handle to clean up any garbage code pasted.
   * Most of this comes from copying from Slack, which has a bad habit of injecting their own characters in your code snippets.
   * I've emailed Slack about the issue and they're working on it. I can't reference a ticket number because their ticket system is entirely internally facing.
   * @param editor The editor the register the paste handler for
   */
  static registerPasteHandler(editor) {
    editor.onDidPaste(pasteEvent => {
      var smartQuotes = /“|”/g;
      var invalidChars = /\u200b/g; // zero width space
      var invalidSpaces = /\u00A0/g; // non-breaking space
      var editorModel = editor.getModel();
      assertNotNull(editorModel);
      var pastedText = editorModel.getValueInRange(pasteEvent.range);
      if (smartQuotes.test(pastedText) || invalidChars.test(pastedText) || invalidSpaces.test(pastedText)) {
        editorModel.applyEdits([{
          range: pasteEvent.range,
          text: pastedText.replace(smartQuotes, '"').replace(invalidChars, '').replace(invalidSpaces, ' ')
        }]);
      }
    });
  }
  static isMacPlatform() {
    var platform = window.navigator.platform;
    return platform.startsWith('Mac');
  }

  /**
   * Remove any keybindings which are used for our own shortcuts.
   * This allows the key events to bubble up so a component higher up can capture
   * them. Note that this is a global configuration, so all editor instances will
   * be impacted.
   */
  static removeConflictingKeybindings() {
    // All editor instances share a global keybinding registry which is where
    // default keybindings are set. There doesn't appear to be a way to remove
    // default bindings, but we can add new ones that will override the existing
    // ones and set `command` to null. This will treat the key events as unhandled
    // and allow them to bubble up.
    monaco.editor.addKeybindingRule(
    // Restart console in Enterprise (see Shortcuts.ts)
    {
      /* eslint-disable-next-line no-bitwise */
      keybinding: monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyD,
      command: null
    });

    // Ctrl+H is used to focus Community console history in Windows + Linux.
    // An alternate shortcut is used for Mac, so no need to override it
    // (See ConsoleShortcuts.ts)
    if (!MonacoUtils.isMacPlatform()) {
      monaco.editor.addKeybindingRule({
        /* eslint-disable-next-line no-bitwise */
        keybinding: monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyH,
        command: null
      });
    }
  }

  /**
   * Creates actions with a `noop` run function to disable specified keybindings.
   * Note that this will swallow the events. To disable default keybindings in a
   * way that allows events to propagate upward, see `removeConflictingKeybindings`
   * @param editor Editor to disable keybindings for
   * @param keybindings List of monaco keybindings to disable. Often a bitwise
   * combination like `monaco.KeyMod.Alt | monaco.KeyMod.KeyJ`
   */
  static disableKeyBindings(editor, keybindings) {
    editor.addAction({
      // This shouldn't be referenced by anything so using an arbitrary unique id
      id: "disable-keybindings-".concat(nanoid()),
      label: '',
      // This action won't be shown in the UI so no need for a label
      keybindings,
      run: () => undefined
    });
  }
  static getMonacoKeyCodeFromShortcut(shortcut) {
    var keyState = shortcut.keyState;
    var keyValue = keyState.keyValue;
    if (keyValue === null) {
      return 0;
    }
    var isMac = MonacoUtils.isMacPlatform();
    if (isMac) {
      return (
        // eslint-disable-next-line no-bitwise
        (keyState.metaKey ? monaco.KeyMod.CtrlCmd : 0) | (keyState.shiftKey ? monaco.KeyMod.Shift : 0) | (keyState.altKey ? monaco.KeyMod.Alt : 0) | (keyState.ctrlKey ? monaco.KeyMod.WinCtrl : 0) | KeyCodeUtils.fromString(keyValue)
      );
    }
    return (
      // eslint-disable-next-line no-bitwise
      (keyState.ctrlKey ? monaco.KeyMod.CtrlCmd : 0) | (keyState.shiftKey ? monaco.KeyMod.Shift : 0) | (keyState.altKey ? monaco.KeyMod.Alt : 0) | (keyState.metaKey ? monaco.KeyMod.WinCtrl : 0) | KeyCodeUtils.fromString(keyValue)
    );
  }
  static provideLinks(model) {
    var newTokens = [];
    var _loop = function _loop(i) {
      var lineText = model.getLineContent(i);
      var originalTokens = linkifyFind(lineText);
      var tokens = originalTokens.filter(token => {
        if (token.type === 'url') {
          return /^https?:\/\//.test(token.value);
        }
        return true;
      });
      // map the tokens to the ranges - you know the line number now, use the token start/end as the startColumn/endColumn
      tokens.forEach(token => {
        newTokens.push({
          url: token.href,
          range: new monaco.Range(i, token.start + 1, i, token.end + 1)
        });
      });
    };
    for (var i = 1; i <= model.getLineCount(); i += 1) {
      _loop(i);
    }
    return {
      links: newTokens
    };
  }

  /**
   * Generates a console URI for use with monaco.
   * @returns A new console URI
   */
  static generateConsoleUri() {
    return monaco.Uri.parse("".concat(CONSOLE_URI_PREFIX).concat(nanoid()));
  }

  /**
   * Checks if a model is a console model based on its URI.
   * @param model The monaco model to check
   * @returns If the model is a console model
   */
  static isConsoleModel(model) {
    return model.uri.toString().startsWith(CONSOLE_URI_PREFIX);
  }

  /**
   * Checks if the editor has the formatDocument action registered.
   * @param editor The monaco editor to check
   * @returns If the editor has a document formatter registered
   */
  static canFormat(editor) {
    var _editor$getAction;
    return ((_editor$getAction = editor.getAction('editor.action.formatDocument')) === null || _editor$getAction === void 0 ? void 0 : _editor$getAction.isSupported()) === true;
  }

  /**
   * Runs the formatDocument action on the editor.
   * @param editor The editor to format
   */
  static formatDocument(editor) {
    return _asyncToGenerator(function* () {
      var _editor$getAction2;
      yield (_editor$getAction2 = editor.getAction('editor.action.formatDocument')) === null || _editor$getAction2 === void 0 ? void 0 : _editor$getAction2.run();
    })();
  }
}
export default MonacoUtils;
//# sourceMappingURL=MonacoUtils.js.map